import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Stack, Button, IconButton, TextField, MenuItem,
  Divider, Alert, Snackbar,
} from '@mui/material';
import { ArrowBack, TaskAlt, Cancel } from '@mui/icons-material';
import { procurementService } from '../../services/procurementService';
import { formatIndianNumber } from '../../utils/currency';

const n = (v: string) => Number(v) || 0;
const r2 = (x: number) => Math.round(x * 100) / 100;
const MODES = ['NEFT', 'RTGS', 'IMPS', 'UPI', 'CHEQUE', 'CASH', 'BANK_TRANSFER'];
const SECTIONS = ['', '194C', '194J', '194I', '194H', '194A', '194Q'];

const VendorPaymentNewPage: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<any[]>([]);
  const [f, setF] = useState({
    vendorId: '', paymentDate: new Date().toISOString().slice(0, 10), grossAmount: '',
    tdsSection: '', tdsAmount: '', paymentMode: 'NEFT', bankReference: '', notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { procurementService.getVendors().then((v: any) => setVendors(v.vendors || v || [])).catch(() => {}); }, []);
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));

  const net = useMemo(() => r2(n(f.grossAmount) - n(f.tdsAmount)), [f.grossAmount, f.tdsAmount]);
  const valid = f.vendorId && n(f.grossAmount) > 0;

  const submit = async () => {
    setError(null);
    if (!valid) { setError('Vendor and a gross amount are required'); return; }
    setSaving(true);
    try {
      await procurementService.createVendorPayment({
        vendorId: f.vendorId, paymentDate: f.paymentDate, grossAmount: n(f.grossAmount),
        tdsSection: f.tdsSection || undefined, tdsAmount: n(f.tdsAmount) || 0, netAmount: net,
        paymentMode: f.paymentMode, bankReference: f.bankReference || undefined, notes: f.notes || undefined,
      });
      setToast('Payment created');
      setTimeout(() => navigate('/procurement/payments'), 800);
    } catch (e: any) { setError(e.message || 'Failed to create payment'); } finally { setSaving(false); }
  };

  const vName = (id: string) => { const v = vendors.find((x) => String(x.id) === String(id)); return v ? (v.company_name || v.name) : ''; };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/procurement/payments')}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>New Vendor Payment</Typography>
            <Typography variant="body2" color="text.secondary">Pay a supplier with TDS and see the bank &amp; ledger impact.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<Cancel />} onClick={() => navigate('/procurement/payments')}>Cancel</Button>
          <Button variant="contained" startIcon={<TaskAlt />} onClick={submit} disabled={saving || !valid}>Create Payment</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card variant="outlined"><CardContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Supplier" value={f.vendorId} onChange={(e) => set({ vendorId: e.target.value })}>
                  {vendors.map((v) => <MenuItem key={v.id} value={v.id}>{v.company_name || v.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth size="small" type="date" label="Payment Date" InputLabelProps={{ shrink: true }} value={f.paymentDate} onChange={(e) => set({ paymentDate: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField select fullWidth size="small" label="Payment Mode" value={f.paymentMode} onChange={(e) => set({ paymentMode: e.target.value })}>
                  {MODES.map((m) => <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="Gross Amount" value={f.grossAmount} onChange={(e) => set({ grossAmount: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField select fullWidth size="small" label="TDS Section" value={f.tdsSection} onChange={(e) => set({ tdsSection: e.target.value })}>
                  {SECTIONS.map((s) => <MenuItem key={s || 'none'} value={s}>{s || 'None'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth size="small" type="number" label="TDS Amount" value={f.tdsAmount} onChange={(e) => set({ tdsAmount: e.target.value })} /></Grid>
              <Grid size={{ xs: 6, sm: 6 }}><TextField fullWidth size="small" label="Bank Reference" value={f.bankReference} onChange={(e) => set({ bankReference: e.target.value })} /></Grid>
              <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Notes" value={f.notes} onChange={(e) => set({ notes: e.target.value })} /></Grid>
            </Grid>
          </CardContent></Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ position: 'sticky', top: 16 }}><CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Payment</Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Row label="Gross" value={formatIndianNumber(n(f.grossAmount))} />
              <Row label="TDS" value={`- ${formatIndianNumber(n(f.tdsAmount))}`} />
              <Divider sx={{ my: 0.5 }} />
              <Row label="Net to Bank" value={formatIndianNumber(net)} bold />
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Accounting Preview</Typography>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>Dr Accounts Payable {formatIndianNumber(n(f.grossAmount))}</Typography>
              {n(f.tdsAmount) > 0 && <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{'  '}Cr TDS Payable {formatIndianNumber(n(f.tdsAmount))}</Typography>}
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{'  '}Cr Bank {formatIndianNumber(net)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Posts on execution; the TDS deduction is recorded in the 26Q register.</Typography>
            </Stack>
            {f.vendorId && <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>Paying: <b>{vName(f.vendorId)}</b></Typography>}
          </CardContent></Card>
        </Grid>
      </Grid>
      <Snackbar open={!!toast} autoHideDuration={2000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500 }}>{value}</Typography>
  </Stack>
);

export default VendorPaymentNewPage;
